import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  changeEmployeeSelfPin,
  fetchEmployeeSelfHistory,
  identifyAttendanceEmployee
} from '../api/attendance';
import { fetchCurrentAnnouncements } from '../api/announcements';
import { getErrorMessage } from '../api/client';
import { API_BASE_URL } from '../config';
import { resolveAttendancePhotoUrl } from '../utils/resolveAttendancePhotoUrl';
import { announcementRepeatLabel } from '../utils/announcementRepeat';
import AttendanceKiosk from './AttendanceKiosk';
import FormsManager from './admin/FormsManager';

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function monthStartIso() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function formatDateTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-PH', {
    timeZone: 'Asia/Manila',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

function formatAnnouncementDate(value) {
  if (!value) return '-';
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString('en-PH', {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  });
}

function announcementRangeLabel(item) {
  if (!item) return '';
  const start = formatAnnouncementDate(item.eventDate);
  const end = item.endDate && item.endDate !== item.eventDate ? formatAnnouncementDate(item.endDate) : '';
  return end ? `${start} to ${end}` : start;
}

export default function EmployeePortalModal({ visible, onClose, onAdminLogin }) {
  const [pin, setPin] = useState('');
  const [employee, setEmployee] = useState(null);
  const [records, setRecords] = useState([]);
  const [from, setFrom] = useState(monthStartIso());
  const [to, setTo] = useState(todayIso());
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [portalView, setPortalView] = useState('kiosk');
  const [announcementData, setAnnouncementData] = useState({ todayEvents: [], upcoming: null });
  const [announcementLoading, setAnnouncementLoading] = useState(false);
  const [announcementError, setAnnouncementError] = useState('');
  const sessionVersion = useRef(0);
  const employeeId = employee?._id || employee?.id;
  const formsAuthConfig = useMemo(() => ({
    employeeForms: true,
    headers: { 'X-Employee-ID': employeeId, 'X-Employee-PIN': pin }
  }), [employeeId, pin]);

  const signOut = useCallback((notice = '') => {
    sessionVersion.current += 1;
    setEmployee(null);
    setRecords([]);
    setPin('');
    setNewPin('');
    setConfirmPin('');
    setPortalView('kiosk');
    setAnnouncementData({ todayEvents: [], upcoming: null });
    setAnnouncementError('');
    setAnnouncementLoading(false);
    setLoading(false);
    setPinLoading(false);
    setMessage('');
    setError(notice);
  }, []);

  const handleFormsUnauthorized = useCallback(() => {
    signOut('Please enter your employee PIN again to access Forms.');
  }, [signOut]);

  useEffect(() => {
    if (!visible) signOut();
  }, [visible, signOut]);

  const groupedRows = useMemo(() => {
    const byDate = new Map();
    records.forEach((record) => {
      const d = record.timestamp ? new Date(record.timestamp) : null;
      const key = d
        ? d.toLocaleDateString('en-CA', { timeZone: 'Asia/Manila' })
        : 'Unknown';
      if (!byDate.has(key)) byDate.set(key, { date: key, in: '', out: '', note: '', inPhoto: '', outPhoto: '', reportPhoto: '' });
      const row = byDate.get(key);
      if (record.type === 'in' && !row.in) {
        row.in = formatDateTime(record.timestamp);
        row.inPhoto = record.facePhotoUrl || '';
      }
      if (record.type === 'out' && !row.out) {
        row.out = formatDateTime(record.timestamp);
        row.note = record.workNote || '';
        row.outPhoto = record.facePhotoUrl || '';
        row.reportPhoto = record.reportPhotoUrl || '';
      }
    });
    return Array.from(byDate.values()).sort((a, b) => b.date.localeCompare(a.date));
  }, [records]);

  const loadDtr = useCallback(async (emp = employee, currentPin = pin) => {
    if (!emp?._id && !emp?.id) return;
    const version = sessionVersion.current;
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const data = await fetchEmployeeSelfHistory({
        employeeId: emp._id || emp.id,
        pin: currentPin,
        from,
        to
      });
      if (version !== sessionVersion.current) return;
      setRecords(Array.isArray(data.records) ? data.records : []);
      if (data.employee) setEmployee((prev) => ({ ...(prev || {}), ...data.employee }));
    } catch (err) {
      if (version !== sessionVersion.current) return;
      setError(getErrorMessage(err, 'Failed to load DTR.'));
    } finally {
      if (version === sessionVersion.current) setLoading(false);
    }
  }, [employee, pin, from, to]);

  const loadAnnouncements = useCallback(async (emp = employee) => {
    if (!emp?._id && !emp?.id) return;
    const version = sessionVersion.current;
    setAnnouncementLoading(true);
    setAnnouncementError('');
    try {
      const data = await fetchCurrentAnnouncements({
        employeeId: emp._id || emp.id,
        storeId: emp.storeId || undefined
      });
      if (version !== sessionVersion.current) return;
      setAnnouncementData({
        todayEvents: Array.isArray(data.todayEvents) ? data.todayEvents : [],
        upcoming: data.upcoming || null
      });
    } catch (err) {
      if (version !== sessionVersion.current) return;
      setAnnouncementData({ todayEvents: [], upcoming: null });
      setAnnouncementError(getErrorMessage(err, 'Could not load announcements.'));
    } finally {
      if (version === sessionVersion.current) setAnnouncementLoading(false);
    }
  }, [employee]);

  const handleVerify = useCallback(async (event) => {
    event.preventDefault();
    const cleanPin = pin.trim();
    if (!cleanPin) {
      setError('Enter your employee PIN.');
      return;
    }
    setError('');
    setMessage('');
    setLoading(true);
    const version = ++sessionVersion.current;
    try {
      const emp = await identifyAttendanceEmployee({ pin: cleanPin });
      if (version !== sessionVersion.current) return;
      const data = await fetchEmployeeSelfHistory({
        employeeId: emp._id || emp.id,
        pin: cleanPin,
        from,
        to
      });
      if (version !== sessionVersion.current) return;
      setEmployee(emp);
      setPin(cleanPin);
      setPortalView('kiosk');
      loadAnnouncements(emp);
      setRecords(Array.isArray(data.records) ? data.records : []);
    } catch (err) {
      if (version !== sessionVersion.current) return;
      setEmployee(null);
      setRecords([]);
      setAnnouncementData({ todayEvents: [], upcoming: null });
      setError(getErrorMessage(err, 'Invalid PIN.'));
    } finally {
      if (version === sessionVersion.current) setLoading(false);
    }
  }, [pin, from, to, loadAnnouncements]);

  const handleChangePin = useCallback(async (event) => {
    event.preventDefault();
    if (!employee) return;
    if (newPin !== confirmPin) {
      setError('New PIN and confirm PIN do not match.');
      return;
    }
    if (!/^\d{4,12}$/.test(newPin.trim())) {
      setError('New PIN must be 4 to 12 digits.');
      return;
    }
    setError('');
    setMessage('');
    setPinLoading(true);
    const version = sessionVersion.current;
    try {
      await changeEmployeeSelfPin({
        employeeId: employee._id || employee.id,
        currentPin: pin,
        newPin
      });
      if (version !== sessionVersion.current) return;
      setPin(newPin.trim());
      setNewPin('');
      setConfirmPin('');
      setMessage('PIN updated.');
    } catch (err) {
      if (version !== sessionVersion.current) return;
      setError(getErrorMessage(err, 'Failed to update PIN.'));
    } finally {
      if (version === sessionVersion.current) setPinLoading(false);
    }
  }, [employee, pin, newPin, confirmPin]);

  const resetAndClose = () => {
    signOut();
    onClose?.();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="employee-portal-title">
      <div className={`flex max-h-[92vh] w-full ${portalView === 'forms' ? 'max-w-7xl' : 'max-w-4xl'} flex-col overflow-hidden rounded-2xl bg-white shadow-2xl`}>
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 id="employee-portal-title" className="text-xl font-bold text-slate-900">Employee Portal</h2>
            <p className="text-sm text-slate-500">View your DTR, create forms, and update your PIN.</p>
          </div>
          <button type="button" onClick={resetAndClose} className="rounded-full px-3 py-1 text-2xl leading-none text-slate-500 hover:bg-slate-100" aria-label="Close">
            x
          </button>
        </div>

        <div className="overflow-auto p-5">
          {error ? <p className="mb-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p> : null}
          {message ? <p className="mb-3 rounded border border-green-200 bg-green-50 p-3 text-sm text-green-700">{message}</p> : null}

          {!employee ? (
            <form onSubmit={handleVerify} className="mx-auto max-w-sm space-y-3">
              <label className="block text-sm font-medium text-slate-700" htmlFor="employeePortalPin">Employee PIN</label>
              <input
                id="employeePortalPin"
                type="password"
                inputMode="numeric"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                className="w-full rounded-lg border px-3 py-3 text-lg"
                placeholder="Enter PIN"
                autoComplete="off"
              />
              <button type="submit" disabled={loading || !pin.trim()} className="w-full rounded-lg bg-[#5c2d91] py-3 font-semibold text-white disabled:opacity-50">
                {loading ? 'Checking...' : 'Open Employee Portal'}
              </button>
              <button
                type="button"
                onClick={() => {
                  resetAndClose();
                  onAdminLogin?.();
                }}
                className="w-full rounded-lg border py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Admin login
              </button>
            </form>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border bg-slate-50 p-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Employee</p>
                  <p className="text-lg font-bold text-slate-900">{employee.name}</p>
                  {employee.role ? <p className="text-sm text-slate-600">{employee.role}</p> : null}
                </div>
                <button type="button" onClick={() => signOut()} className="rounded-lg border bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100">
                  Sign out
                </button>
              </div>

              <div className="rounded-xl border border-indigo-100 bg-indigo-50/70 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h3 className="font-semibold text-slate-900">Calendar notices</h3>
                    <p className="text-xs text-slate-600">Today's event and next upcoming schedule.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => loadAnnouncements(employee)}
                    className="rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                  >
                    Refresh
                  </button>
                </div>
                {announcementLoading ? (
                  <p className="mt-3 text-sm text-slate-600">Loading notices...</p>
                ) : announcementError ? (
                  <p className="mt-3 rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800">{announcementError}</p>
                ) : (
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <div className="rounded-lg bg-white p-3 shadow-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-green-700">Today</p>
                      {announcementData.todayEvents.length ? (
                        <div className="mt-2 space-y-2">
                          {announcementData.todayEvents.map((item) => (
                            <div key={item._id || item.id} className="border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                              <p className="font-semibold text-slate-900">{item.title}</p>
                              <p className="text-xs text-slate-500">{announcementRangeLabel(item)}</p>
                              {announcementRepeatLabel(item.repeat) ? <p className="text-xs text-indigo-700">Repeats: {announcementRepeatLabel(item.repeat)}</p> : null}
                              {item.message ? <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{item.message}</p> : null}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-2 text-sm text-slate-500">No event scheduled for today.</p>
                      )}
                    </div>
                    <div className="rounded-lg bg-white p-3 shadow-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Next upcoming</p>
                      {announcementData.upcoming ? (
                        <div className="mt-2">
                          <p className="font-semibold text-slate-900">{announcementData.upcoming.title}</p>
                          <p className="text-xs text-slate-500">{announcementRangeLabel(announcementData.upcoming)}</p>
                          {announcementRepeatLabel(announcementData.upcoming.repeat) ? <p className="text-xs text-indigo-700">Repeats: {announcementRepeatLabel(announcementData.upcoming.repeat)}</p> : null}
                          {announcementData.upcoming.message ? (
                            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{announcementData.upcoming.message}</p>
                          ) : null}
                        </div>
                      ) : (
                        <p className="mt-2 text-sm text-slate-500">No upcoming event scheduled.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 rounded-xl border bg-slate-50 p-1">
                {[
                  { id: 'kiosk', label: 'Clock In / Out' },
                  { id: 'dtr', label: 'My DTR' },
                  { id: 'forms', label: 'Forms' },
                  { id: 'pin', label: 'Change PIN' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setPortalView(tab.id)}
                    className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                      portalView === tab.id
                        ? 'bg-[#5c2d91] text-white shadow-sm'
                        : 'text-slate-700 hover:bg-white'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {portalView === 'kiosk' && (
                <AttendanceKiosk
                  initialPin={pin}
                  initialEmployee={employee}
                  showTitle
                  shellClassName="space-y-4 rounded-xl border bg-white p-4"
                  onRecorded={() => loadDtr(employee, pin)}
                />
              )}

              {portalView === 'forms' && (
                <section className="rounded-xl border bg-white p-4" aria-label="Employee forms">
                  <FormsManager
                    key={employeeId}
                    employeeMode
                    authConfig={formsAuthConfig}
                    onUnauthorized={handleFormsUnauthorized}
                  />
                </section>
              )}

              {portalView === 'dtr' && (
                <div className="rounded-xl border p-4">
                  <div className="mb-3 flex flex-wrap items-end gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-600" htmlFor="dtrFrom">From</label>
                      <input id="dtrFrom" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded border px-2 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600" htmlFor="dtrTo">To</label>
                      <input id="dtrTo" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded border px-2 py-2 text-sm" />
                    </div>
                    <button type="button" onClick={() => loadDtr()} disabled={loading} className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                      {loading ? 'Loading...' : 'Load DTR'}
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-100 text-left text-slate-700">
                        <tr>
                          <th className="p-2">Date</th>
                          <th className="p-2">Clock In</th>
                          <th className="p-2">Clock Out</th>
                          <th className="p-2">Photos</th>
                          <th className="p-2">Work note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupedRows.length === 0 ? (
                          <tr><td className="p-4 text-center text-slate-500" colSpan={5}>No DTR records for this range.</td></tr>
                        ) : groupedRows.map((row) => (
                          <tr key={row.date} className="border-b">
                            <td className="p-2 font-medium">{row.date}</td>
                            <td className="p-2">{row.in || '-'}</td>
                            <td className="p-2">{row.out || '-'}</td>
                            <td className="p-2">
                              <div className="flex flex-wrap gap-2">
                                {[
                                  { label: 'In', url: row.inPhoto },
                                  { label: 'Out', url: row.outPhoto },
                                  { label: 'Report', url: row.reportPhoto }
                                ].filter((p) => p.url).map((photo) => {
                                  const src = resolveAttendancePhotoUrl(photo.url, API_BASE_URL);
                                  return (
                                    <a key={photo.label} href={src} target="_blank" rel="noreferrer" className="group block">
                                      <img
                                        src={src}
                                        alt={`${photo.label} attendance`}
                                        className="h-14 w-14 rounded border object-cover group-hover:ring-2 group-hover:ring-[#5c2d91]"
                                        loading="lazy"
                                      />
                                      <span className="mt-1 block text-center text-[10px] text-slate-500">{photo.label}</span>
                                    </a>
                                  );
                                })}
                                {!row.inPhoto && !row.outPhoto && !row.reportPhoto ? (
                                  <span className="text-slate-400">-</span>
                                ) : null}
                              </div>
                            </td>
                            <td className="p-2 max-w-sm truncate" title={row.note}>{row.note || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {portalView === 'pin' && (
                <form onSubmit={handleChangePin} className="rounded-xl border p-4">
                  <h3 className="mb-3 font-semibold text-slate-900">Change PIN</h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <input type="password" inputMode="numeric" value={newPin} onChange={(e) => setNewPin(e.target.value)} className="rounded border px-3 py-2" placeholder="New PIN" autoComplete="new-password" />
                    <input type="password" inputMode="numeric" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value)} className="rounded border px-3 py-2" placeholder="Confirm new PIN" autoComplete="new-password" />
                  </div>
                  <button type="submit" disabled={pinLoading || !newPin || !confirmPin} className="mt-3 rounded bg-[#5c2d91] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                    {pinLoading ? 'Updating...' : 'Update PIN'}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
